import { useCallback, useEffect, useRef } from 'react';

import type { CloneController, CloneField, CloneRow, CloneType, ResolvedCloneRows } from '../assistant/cloneActions';
import { drainCrudClone, registerCloneController, unregisterCloneController } from '../assistant/cloneRegistry';

import { useFeatureToggle } from './useFeatureToggle';

/**
 * How soon a mounted list looks again for a proposal whose rows it could not look up, doubling up to the cap:
 * each attempt is a request per row named, and a failing lookup would otherwise repeat them ten times a second.
 */
const DRAIN_RETRY_MS = 100;
const DRAIN_RETRY_MAX_MS = 2_000;

/** What a list hands over so the assistant can clone from it. */
export interface CrudCloneSource {
  /** The clone table's own columns, and what may be done to each. */
  fields: readonly CloneField[];
  /** The rows for these keys, or the user's current selection when none are named. */
  resolveRows: (keys: readonly string[], signal?: AbortSignal) => Promise<ResolvedCloneRows>;
  openWith: (rows: CloneRow[], originals: CloneRow[], cloneType: CloneType) => void;
}

/**
 * Lets the assistant open this list's clone table, carrying the changes it proposes, while the list is
 * mounted.
 *
 * The list passes the columns its clone actually has, how to resolve the rows it holds, and the opener; all
 * three are read through this on every attempt, so what answers is the list as it is now. A proposal that
 * arrived while the user was elsewhere is drained here as the list comes up, retrying while looking its rows
 * up fails.
 *
 * Gated on `enableAssistant`, like the page context: with the flag off nothing is registered, so a tenant
 * without Ohm has no list the assistant can clone from.
 */
export const useCrudClone = (entityType: string | undefined, source: CrudCloneSource): void => {
  const enabled = useFeatureToggle(['enableAssistant']) && entityType !== undefined;

  // Read at call time rather than captured, so a fresh proposal reaches the list's current rows and opener,
  // and the registration does not churn on every render the list does.
  const current = useRef(source);
  current.current = source;
  const registered = useRef(false);

  // Opening throws once the list has gone while its rows were being looked up, so the proposal is not spent on
  // a list nobody sees and a drain puts it back.
  const readController = useCallback(
    (): CloneController => ({
      fields: () => [...current.current.fields],
      rows: (keys, signal) => current.current.resolveRows(keys, signal),
      open: (rows, originals, cloneType) => {
        if (!registered.current) throw new Error('The list is no longer open.');
        current.current.openWith(rows, originals, cloneType);
      },
    }),
    [],
  );

  useEffect(() => {
    if (!enabled || entityType === undefined) return;

    registerCloneController(entityType, readController);
    registered.current = true;

    let retry: ReturnType<typeof setTimeout> | undefined;
    let unmounted = false;
    let delay = DRAIN_RETRY_MS;
    const drain = async (): Promise<void> => {
      if ((await drainCrudClone(entityType, readController)) || unmounted) return;
      retry = setTimeout(drain, delay);
      delay = Math.min(delay * 2, DRAIN_RETRY_MAX_MS);
    };
    void drain();

    return () => {
      unmounted = true;
      registered.current = false;
      clearTimeout(retry);
      unregisterCloneController(entityType, readController);
    };
  }, [enabled, entityType, readController]);
};
